import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { entities } from "byzantine";
import {
  AccountNumberTextInput,
  StateSelector,
  ZipCodeTextInput,
  AutocompleteStreetAddress,
} from "cerulean";
import {
  Alert,
  CollapsibleCard,
  ResponsiveFlex,
  Button,
  Row,
  TextInput,
} from "@narmi/design_system";
import Address from "byzantine/src/Address";
import * as formAdapters from "../../src/adapters";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import SectionHeader from "./SectionHeader";
import { useDrawerContext } from "./DrawerValidationsProvider";
import { WireSectionProps } from ".";
import RoutingNumberInput from "./RoutingNumberInput";
import styles from "./RecipientDrawer.module.scss";

const WireDestinationSection = ({
  title,
  optionalLabel,
  isOpen,
  onClose,
  onSave,
  onOpen,
  savedACHDestination,
  recipientId,
  fromDomesticWireFlow,
  achInitialValues,
}: WireSectionProps) => {
  const { l10n } = useLocalization();
  const { disallow_wire_address_pobox: disallowAddressPoBox } =
    useUserFeatures();
  const { form, handleSubmitUpdate, handleSubmitCreate, loading } =
    entities.recipients.useWireDestinationForm(
      recipientId,
      disallowAddressPoBox
    );
  const { form: achDestinationForm } =
    entities.recipients.useACHDestinationForm(recipientId, achInitialValues);
  const recipient = entities.recipients.useRecipient(recipientId);
  const isAutocompleteApiAvailable = !!(window as any).google?.maps;
  const { triggerValidations, validNotSavedSections } = useDrawerContext();
  const [showCancelButton, setShowCancelButton] = useState(false);
  const shouldShowAlert =
    !recipient?.fedwire_destination && fromDomesticWireFlow;

  useEffect(() => {
    if (recipient?.fedwire_destination?.id) {
      setShowCancelButton(true);
    }
  }, []);

  const { onChange, ...restOfStreetAddressFieldProps } =
    formAdapters.fieldWithOnChange(form, "address.streetAddress");

  const handleClickSave = async () => {
    const onSuccess = () => {
      setShowCancelButton(true);
      // we have to "reset" the form so the form reset's dirty value after being saved
      form.resetForm({ values: form.values });
      onSave();
    };
    if (recipient?.fedwire_destination) {
      handleSubmitUpdate(onSuccess);
    } else {
      handleSubmitCreate(onSuccess);
    }
  };

  const handleClickOpen = () => {
    const { isValid: isDrawerValid } = triggerValidations();
    if (!isDrawerValid) {
      return;
    }
    onOpen();
  };

  const handleClickClose = () => {
    const { isValid: isDrawerValid } = triggerValidations();
    if (!isDrawerValid) {
      return;
    }
    onClose();
  };

  const handleClickCancel = () => {
    form.resetForm();
    onClose();
  };

  const handleOnAddressUpdate = (value: Address) => {
    const {
      street_address: streetAddress,
      city,
      region_code: regionCode,
      postal_code: postalCode,
    } = value;
    form.setFieldValue("address.streetAddress", streetAddress);
    form.setFieldValue("address.city", city);
    form.setFieldValue("address.regionCode", regionCode);
    form.setFieldValue("address.postalCode", postalCode);
  };

  const handleClickUseACH = () => {
    const { accountNumber, routingNumber } = achDestinationForm.values;
    form.setFieldValue("accountNumber", accountNumber);
    form.setFieldValue("routingNumber", routingNumber);
  };

  return (
    <CollapsibleCard
      onClose={handleClickClose}
      onOpen={handleClickOpen}
      title={title}
      isOpen={isOpen}
      trigger="header"
      renderTitle={() => (
        <SectionHeader
          title={title || "Domestic wire"}
          optionalLabel={optionalLabel}
          isOpen={isOpen}
          checkCondition={Boolean(form.values.accountNumber)}
        />
      )}
    >
      <ResponsiveFlex gapSize="s">
        {validNotSavedSections.wire ? (
          <div className="message-content negative">
            <span className="narmi-icon-info margin--right--m" />
            Save details to continue
          </div>
        ) : null}
        <Alert
          kind="warn"
          isActive={shouldShowAlert}
          icon="alert-triangle"
          isDismissable={false}
        >
          <div className="fontSize--s">
            {l10n.getString("add-fedwire-details-error-banner")}
          </div>
        </Alert>
        {savedACHDestination ? (
          <Row justifyContent="end">
            <Row.Item shrink>
              <Button
                kind="plain"
                testId="use-ach-details"
                onClick={handleClickUseACH}
              >
                Use ACH details
              </Button>
            </Row.Item>
          </Row>
        ) : null}
        <AccountNumberTextInput
          label={l10n.getString("labelAccountNumber", null, "Account number")}
          {...formAdapters.fieldWithOnChange(form, "accountNumber")}
        />

        <RoutingNumberInput
          label={l10n.getString("label-routing", null, "Routing number")}
          {...formAdapters.fieldWithOnChange(form, "routingNumber")}
          routingType="fedwire"
        />
        <Row gapSize="xs">
          <Row.Item shrink className={styles.addressGrow}>
            {isAutocompleteApiAvailable ? (
              <AutocompleteStreetAddress
                streetAddressLabel={l10n.getString(
                  "label-street-address",
                  null,
                  "Street address"
                )}
                onChange={onChange}
                onUpdate={handleOnAddressUpdate}
                {...restOfStreetAddressFieldProps}
              />
            ) : (
              <TextInput
                label={l10n.getString(
                  "label-street-address",
                  null,
                  "Street address"
                )}
                {...formAdapters.input(form, "address.streetAddress")}
              />
            )}
          </Row.Item>
          <Row.Item shrink className={styles.addressShrink}>
            <TextInput
              label={l10n.getString(
                "label-address-2",
                null,
                "Apt/Suite (optional)"
              )}
              {...formAdapters.input(form, "address.streetAddress2")}
            />
          </Row.Item>
        </Row>
        <TextInput
          label={l10n.getString("label-city", null, "City")}
          {...formAdapters.input(form, "address.city")}
        />
        <Row gapSize="xs">
          <Row.Item>
            <StateSelector
              label={l10n.getString("label-state", null, "State")}
              {...formAdapters.fieldWithOnChange(form, "address.regionCode")}
            />
          </Row.Item>
          <Row.Item>
            <ZipCodeTextInput
              label={l10n.getString("label-postal", null, "Zip code")}
              {...formAdapters.fieldWithOnChange(form, "address.postalCode")}
            />
          </Row.Item>
        </Row>
        <Row alignItems="center">
          <Row.Item>
            {showCancelButton ? (
              <Button
                kind="negative"
                testId="cancel-wire-destination"
                onClick={handleClickCancel}
              >
                {l10n.getString("button-cancel")}
              </Button>
            ) : null}
          </Row.Item>
          <Row.Item shrink>
            <Button
              kind="primary"
              testId="save-wire-destination"
              onClick={handleClickSave}
              disabled={loading}
              isLoading={loading}
            >
              {l10n.getString("button-save")}
            </Button>
          </Row.Item>
        </Row>
      </ResponsiveFlex>
    </CollapsibleCard>
  );
};

export default WireDestinationSection;
